"use client";

import { c as _c } from "react/compiler-runtime";
import { cn, Divider, Input, Slider } from "@dc/ui";
import type { SliderProps } from "@heroui/react";
import React from "react";
import { useUiLabels } from "../UiLabels/useUiLabels";
import type { RangeFilter, RangeValue } from "./ValueFilter.types";
export type SliderAnimation = "opacity" | "height";
export type ValueFilterProps = Omit<SliderProps, "ref" | "value" | "defaultValue" | "onChange"> & {
  range?: RangeFilter;
  animation?: SliderAnimation;
  value?: RangeValue;
  onValueChange?: (value: RangeValue) => void;
  formatOptions?: Intl.NumberFormatOptions;
};
function clampValue(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
function scaleValue(value: number, fromRange: RangeValue, toRange: RangeValue = [0, 100]) {
  const [fromMin, fromMax] = fromRange;
  const [toMin, toMax] = toRange;
  if (fromMax === fromMin) {
    return toMin;
  }
  const scale = (toMax - toMin) / (fromMax - fromMin);
  return (value - fromMin) * scale + toMin;
}
export type ValueSliderPipProps = {
  isInRange: boolean;
  animation?: SliderAnimation;
};
const ValueSliderPip: React.FC<ValueSliderPipProps> = t0 => {
  const $ = _c(6);
  const {
    animation: t1,
    isInRange
  } = t0;
  const animation = t1 === undefined ? "height" : t1;
  const rand = Math.floor(Math.random() * 100);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = clampValue(rand, 30, 100);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const height = `${t2}%`;
  let t3;
  bb0: {
    if (animation === "height") {
      let t4;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          height
        };
        $[1] = t4;
      } else {
        t4 = $[1];
      }
      let t5;
      if ($[2] !== isInRange) {
        t5 = <span className="relative h-12 w-1 rounded-full bg-default-100 after:absolute after:bottom-0 after:h-0 after:w-full after:rounded-full after:bg-primary after:transition-all after:duration-500! after:content-[''] data-[in-range=true]:after:h-full" data-in-range={isInRange} style={t4} />;
        $[2] = isInRange;
        $[3] = t5;
      } else {
        t5 = $[3];
      }
      t3 = t5;
      break bb0;
    }
    const t4 = isInRange ? "hsl(var(--heroui-primary-500))" : "hsl(var(--heroui-default-100))";
    let t5;
    if ($[4] !== t4) {
      t5 = <span className="h-12 w-1 rounded-full bg-primary transition-all duration-500!" style={{
        background: t4,
        height
      }} />;
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    t3 = t5;
  }
  const pip = t3;
  return pip;
};
export const ValueFilter = React.forwardRef<HTMLDivElement, ValueFilterProps>(({
  range,
  animation,
  value: controlledValue,
  onValueChange,
  formatOptions,
  ...props
}, ref) => {
  const uiLabels = useUiLabels();
  const minBoundary = React.useMemo(() => range?.min ?? 0, [range?.min]);
  const maxBoundary = React.useMemo(() => range?.max ?? 1000, [range?.max]);
  const computedDefaultValue = React.useMemo<RangeValue>(() => {
    const defaultMin = range?.defaultValue?.[0] ?? minBoundary;
    const defaultMax = range?.defaultValue?.[1] ?? maxBoundary;
    return [defaultMin, defaultMax];
  }, [range?.defaultValue, minBoundary, maxBoundary]);
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = React.useState<RangeValue>(computedDefaultValue);
  React.useEffect(() => {
    if (!isControlled) {
      setInternalValue(computedDefaultValue);
    }
  }, [computedDefaultValue, isControlled]);
  const value = isControlled ? controlledValue : internalValue;
  const updateValue = React.useCallback((next: RangeValue) => {
    const normalizedMin = clampValue(next[0], minBoundary, maxBoundary);
    const normalizedMax = clampValue(next[1], minBoundary, maxBoundary);
    const clamped = [Math.min(normalizedMin, normalizedMax), Math.max(normalizedMin, normalizedMax)] as RangeValue;
    if (!isControlled) {
      setInternalValue(clamped);
    }
    onValueChange?.(clamped);
  }, [isControlled, maxBoundary, minBoundary, onValueChange]);
  const rangePercentageValue = React.useMemo(() => {
    const rangeScale: RangeValue = [minBoundary, maxBoundary];
    const minPercentage = Math.floor(scaleValue(value[0], rangeScale));
    const maxPercentage = Math.floor(scaleValue(value[1], rangeScale));
    return [minPercentage, maxPercentage] as const;
  }, [value, minBoundary, maxBoundary]);
  const rangePips = React.useMemo(() => {
    return Array.from({
      length: 32
    }).map((_, index) => {
      const pipValuePercentage = Math.round(scaleValue(index, [0, 31]));
      const isInRange = pipValuePercentage >= rangePercentageValue[0] + 5 && pipValuePercentage <= rangePercentageValue[1] + 2;
      return <ValueSliderPip key={`${index}-${pipValuePercentage}`} animation={animation} isInRange={isInRange} />;
    });
  }, [animation, rangePercentageValue]);
  const handleMinInputValueChange = React.useCallback((inputValue: string) => {
    const newValue = Number(inputValue);
    if (!Number.isNaN(newValue)) {
      const clampedValue = clampValue(newValue, minBoundary, value[1]);
      updateValue([clampedValue, value[1]]);
    }
  }, [minBoundary, updateValue, value]);
  const handleMaxInputValueChange = React.useCallback((inputValue_0: string) => {
    const newValue_0 = Number(inputValue_0);
    if (!Number.isNaN(newValue_0)) {
      const clampedValue_0 = clampValue(newValue_0, value[0], maxBoundary);
      updateValue([value[0], clampedValue_0]);
    }
  }, [maxBoundary, updateValue, value]);
  return <div className={cn("flex flex-col gap-3")} ref={ref}>
        <div className="flex flex-col gap-1">
          <div className="flex h-12 w-full items-end justify-between px-2">{rangePips}</div>
          <Slider {...props} formatOptions={formatOptions ?? {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0
      }} maxValue={maxBoundary} minValue={minBoundary} size="sm" step={range?.step} value={value} onChange={sliderValue => {
        if (!Array.isArray(sliderValue)) return;
        updateValue(sliderValue as RangeValue);
      }} />
        </div>
        <div className="flex items-center">
          <Input aria-label={uiLabels.valueFilter.min} labelPlacement="outside" startContent={<p className="text-default-400">$</p>} type="number" value={`${value[0]}`} onValueChange={handleMinInputValueChange} />
          <Divider className="mx-2 w-2" />
          <Input aria-label={uiLabels.valueFilter.max} labelPlacement="outside" startContent={<p className="text-default-400">$</p>} type="number" value={`${value[1]}`} onValueChange={handleMaxInputValueChange} />
        </div>
      </div>;
});